import React, { ChangeEvent, FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { CameraVideo, CameraVideoOff, Mic, MicMute } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import CopyValueBox from '../CopyValueBox';
import i18next from 'i18next';

export type CallSettings = {
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  selectedCameraId: string | null;
  selectedMicrophoneId: string | null;
}

type MediaDeviceOption = {
  deviceId: string;
  label: string;
}

type CallPreviewProps = {
  roomCode: string;
  mode: 'preview' | 'waiting';
  isSubmitting?: boolean;
  error?: string | null;
  disabled?: boolean;
  onJoin: (settings: CallSettings) => Promise<void>;
  onLeavingWaitingRoom?: () => void;
  onBack?: () => void;
}

const CallPreview: FC<CallPreviewProps> = ({
  roomCode,
  mode,
  isSubmitting = false,
  error = null,
  disabled = false,
  onJoin,
  onLeavingWaitingRoom,
  onBack,
}: CallPreviewProps): ReactElement => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraEnabled, setCameraEnabled] = useState<boolean>(true);
  const [microphoneEnabled, setMicrophoneEnabled] = useState<boolean>(true);

  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string | null>(null);

  const [cameraDevices, setCameraDevices] = useState<MediaDeviceOption[]>([]);
  const [microphoneDevices, setMicrophoneDevices] = useState<MediaDeviceOption[]>([]);

  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const controlsDisabled: boolean = disabled || isSubmitting;
  const isWaitingMode: boolean = mode === 'waiting';

  const stopCurrentStream = useCallback((): void => {
    if (!streamRef.current) {
      return;
    }

    for (const track of streamRef.current.getTracks()) {
      track.stop();
    }

    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const loadDevices = useCallback(async (): Promise<void> => {
    try {
      const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();

      const nextCameraDevices: MediaDeviceOption[] = devices
        .filter((device: MediaDeviceInfo) => device.kind === 'videoinput')
        .map((device: MediaDeviceInfo, index: number) => ({
          deviceId: device.deviceId,
          label: device.label || `${i18next.t('callPreview.camera')} ${index + 1}`,
        }))

      const nextMicrophoneDevices: MediaDeviceOption[] = devices
        .filter((device: MediaDeviceInfo) => device.kind === 'audioinput')
        .map((device: MediaDeviceInfo, index: number) => ({
          deviceId: device.deviceId,
          label: device.label || `${i18next.t('callPreview.microphone')} ${index + 1}`,
        }))

      setCameraDevices(nextCameraDevices);
      setMicrophoneDevices(nextMicrophoneDevices);

      setSelectedCameraId((currentValue: string | null) => {
        if (currentValue && nextCameraDevices.some((device: MediaDeviceOption) => device.deviceId === currentValue)) {
          return currentValue;
        }

        return nextCameraDevices.at(0)?.deviceId ?? null;
      })

      setSelectedMicrophoneId((currentValue: string | null) => {
        if (currentValue && nextMicrophoneDevices.some((device: MediaDeviceOption) => device.deviceId === currentValue)) {
          return currentValue;
        }

        return nextMicrophoneDevices.at(0)?.deviceId ?? null;
      })
    } catch (error) {
      console.error('Failed to enumerate media devices', error);
    }
  }, []);

  const startPreview = useCallback(async (cameraId: string | null, microphoneId: string | null): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPreviewError(i18next.t('callPreview.noCameraPreviewBrowserSupport'));
      setIsPreviewLoading(false);
      return;
    }

    stopCurrentStream();

    if (!cameraEnabled && !microphoneEnabled) {
      return;
    }

    setIsPreviewLoading(true);
    setPreviewError(null);

    try {
      const constraints: MediaStreamConstraints = {
        video: cameraEnabled ? cameraId ? { deviceId: { exact: cameraId } } : true : false,
        audio: microphoneId ? { deviceId: { exact: microphoneId } } : true,
      }

      const nextStream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      for (const track of nextStream.getAudioTracks()) {
        track.enabled = microphoneEnabled;
      }

      streamRef.current = nextStream;

      if (videoRef.current && cameraEnabled) {
        videoRef.current.srcObject = nextStream;
        await videoRef.current.play().catch(() => undefined);
      }

      await loadDevices();
    } catch (error) {
      console.error('Failed to start preview stream', error);
      setPreviewError(i18next.t('callPreview.cameraOrMicrophoneError'));
    } finally {
      setIsPreviewLoading(false);
    }
  }, [cameraEnabled, microphoneEnabled, stopCurrentStream]);

  const toggleMicrophone = useCallback((enabled: boolean): void => {
    setMicrophoneEnabled(enabled);
    if (streamRef.current) {
      for (const track of streamRef.current.getAudioTracks()) {
        track.enabled = enabled;
      }
    }
  }, [])

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', loadDevices);
    return (): void => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
    }
  }, [loadDevices]);

  useEffect(() => {
    void startPreview(selectedCameraId, selectedMicrophoneId);

    const handleUnload = (): void => stopCurrentStream();
    window.addEventListener('beforeunload', handleUnload);

    return (): void => {
      stopCurrentStream();
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [selectedCameraId, selectedMicrophoneId, cameraEnabled]);

  const handleJoin = useCallback(async (): Promise<void> => {
    await onJoin({
      cameraEnabled,
      microphoneEnabled,
      selectedCameraId,
      selectedMicrophoneId
    });
  }, [cameraEnabled, microphoneEnabled, selectedCameraId, selectedMicrophoneId, onJoin]);

  const infoText: string | null = useMemo(() => {
    if (isWaitingMode) {
      return i18next.t('callPreview.waitingInfoText');
    }

    return null;
  }, [isWaitingMode]);

  return (
    <Card
      className="border-0 overflow-hidden shadow-lg w-100"
      style={{
        maxWidth: '1120px',
        minHeight: 'clamp(460px, 55vh, 680px)',
        borderRadius: '1.25rem',
        backgroundColor: '#31424d'
      }}
    >
      <Card.Body className="p-0 d-flex flex-column h-100">
        <Row className="g-0 flex-grow-1 h-100">
          <Col xs={12} lg={8} className="d-flex">
            <div
              className="position-relative w-100 overflow-hidden flex-grow-1"
              style={{
                backgroundColor: '#1c2b3f',
                borderRadius: '1.25rem 0 0 1.25rem',
                minHeight: '280px',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={cameraEnabled && !previewError ? 'position-absolute top-0 start-0 w-100 h-100 d-block' : 'd-none'}
                style={{
                  objectFit: 'cover',
                }}
              />

              {(!cameraEnabled || previewError) && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center px-4 text-white">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '72px',
                      height: '72px',
                      backgroundColor: 'rgba(255, 255, 255, 0.12)'
                    }}
                  >
                    <CameraVideoOff size={32} />
                  </div>

                  <div className="fw-semibold mb-2">
                    {previewError ? i18next.t('callPreview.noPreviewAvailable') : i18next.t('callPreview.cameraDeactivated')}
                  </div>

                  <div className="small opacity-75">
                    {previewError ?? i18next.t('callPreview.joinWithoutCamera')}
                  </div>
                </div>
              )}

              {isPreviewLoading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white">
                  <Spinner animation="border" className="mb-3" />
                  <span className="small">{i18next.t('callPreview.loadPreview')}</span>
                </div>
              )}

              <div
                className="position-absolute start-50 translate-middle-x px-3 py-2"
                style={{
                  bottom: '16px',
                  backgroundColor: 'rgba(32, 43, 52, 0.88)',
                  borderRadius: '999px',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Stack direction="horizontal" gap={2}>
                  <Button
                    variant={cameraEnabled ? 'light' : 'danger'}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border-0"
                    style={{ width: '48px', height: '48px' }}
                    disabled={controlsDisabled}
                    onClick={() => setCameraEnabled((currentValue: boolean) => !currentValue)}
                    aria-label={cameraEnabled ? i18next.t('callPreview.deactivateCamera') : i18next.t('callPreview.activateCamera')}
                  >
                    {cameraEnabled ? <CameraVideo size={18} /> : <CameraVideoOff size={18} /> }
                  </Button>

                  <Button
                    variant={microphoneEnabled ? 'light' : 'danger'}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border-0"
                    style={{ width: '48px', height: '48px' }}
                    disabled={controlsDisabled}
                    onClick={() => toggleMicrophone(!microphoneEnabled)}
                    aria-label={i18next.t(microphoneEnabled ? 'callPreview.deactivateMicrophone' : 'callPreview.activateMicrophone')}
                  >
                    {microphoneEnabled ? <Mic size={18} /> : <MicMute size={18} /> }
                  </Button>
                </Stack>
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4}>
            <div
              className="h-100 d-flex flex-column"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)' }}
            >
              <div className="p-4 p-md-4 p-lg-4">
                <div className="text-white fw-bold mb-2" style={{ fontSize: '1.75rem', lineHeight: 1.1 }}>
                  {i18next.t('callPreview.title')}
                </div>

                <div className="mb-4" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>
                  {i18next.t('callPreview.subTitle')}
                </div>

                {(error || previewError) && (
                  <Alert variant="danger" className="mb-4">
                    {error ?? previewError}
                  </Alert>
                )}

                <div className="mb-4">
                  <div
                    className="text-uppercase fw-semibold mb-2"
                    style={{
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontSize: '0.8rem',
                      letterSpacing: '0.04rem',
                    }}
                  >
                    {i18next.t('callPreview.roomCode')}
                  </div>

                  <CopyValueBox value={roomCode ?? ''} valueCopy={window.location.href} />
                </div>

                <Row className="g-3 mb-4">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-white">{i18next.t('callPreview.camera')}</Form.Label>
                      <Form.Select
                        value={selectedCameraId ?? ''}
                        disabled={controlsDisabled || cameraDevices.length === 0}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedCameraId(event.target.value || null)}
                      >
                        {cameraDevices.length === 0 && (
                          <option value="">{i18next.t('callPreview.noCameraFound')}</option>
                        )}

                        {cameraDevices.map((device: MediaDeviceOption) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-white">{i18next.t('callPreview.microphone')}</Form.Label>
                      <Form.Select
                        value={selectedMicrophoneId ?? ''}
                        disabled={controlsDisabled || microphoneDevices.length === 0}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedMicrophoneId(event.target.value || null)}
                      >
                        {microphoneDevices.length === 0 && (
                          <option value="">{i18next.t('callPreview.noMicrophoneFound')}</option>
                        )}

                        {microphoneDevices.map((device: MediaDeviceOption) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div
                className="mt-auto px-4 py-4"
                style={{ backgroundColor: 'rgba(17, 28, 37, 0.45)', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}
              >
                {infoText && (
                  <div
                    className="text-center mb-3"
                    style={{
                      color: 'rgba(255,255,255,0.88)',
                      fontSize: '0.95rem',
                    }}
                  >
                    {infoText}
                  </div>
                )}

                {!isWaitingMode && (
                  <>
                    <div className="d-grid">
                      <Button
                        variant="primary"
                        disabled={controlsDisabled || isPreviewLoading}
                        onClick={() => {
                          void handleJoin();
                        }}
                        className="fw-semibold"
                        style={{ minHeight: '48px' }}
                      >
                        {isSubmitting && (
                          <Spinner animation="border" size="sm" className="me-2" />
                        )}
                        {i18next.t('callPreview.joinCall')}
                      </Button>
                    </div>

                    {onBack && (
                      <div className="d-grid mt-2">
                        <Button
                          variant="outline-light"
                          disabled={controlsDisabled}
                          onClick={onBack}
                          className="fw-semibold"
                          style={{ minHeight: '48px' }}
                        >
                          {i18next.t('generic.back')}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {isWaitingMode && onLeavingWaitingRoom && (
                  <div className="d-grid">
                    <Button
                      variant="outline-light"
                      disabled={controlsDisabled}
                      onClick={onLeavingWaitingRoom}
                      className="fw-semibold"
                      style={{ minHeight: '48px' }}
                    >
                      {i18next.t('callPreview.leaveWaitingRoom')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>

  )
}

export default CallPreview